import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, withRouter } from "common/src/router/compat";
import { SERVER_SETUP } from "common/src/helpers/constants";
import { gameCategoryToRoute } from "common/src/helpers/functions";
import { Game } from "./Game";
import { useTranslation } from "react-i18next";
import { ReactComponent as Telescope } from "common/src/assets/icon-component/telescope.svg";
import { isTablet } from "react-device-detect";
import {
  AlignCategoryTitle,
  CategoryTitle,
  EmptyCategoriy,
  GameCategoryContent,
  GameCategoryHeader,
  GameCategoryTitle,
  GamesGriddy,
  ShowAllArrow,
  ShowAllGames,
} from "../styledComponents/StyledGame";
import { GameThemeProviders } from "./GameThemeProviders";
/* import DGA from "../helpers/dgaAPI"; */

const isRoyale99Category = (name = "") =>
  String(name)
    .toLowerCase()
    .replace(/[\s_-]/g, "") === "99royale";

const Lobby = ({
  gameLobby,
  gamesForCategory,
  selectedCategory,
  width,
  skin,
  gameImagePath,
  config,
  country,
  mobGamesInLobby,
  providerIsChosen,
  setSelectedGameType,
  setThemeSearchTerm,
  themeSearchTerm,
  setSearchResults,
  accountRoute,
  setSpinner,
  setThemeExists,
  setSelectedCategory,
  themes,
  providerSearchFlag = false,
}) => {
  const { t, i18n } = useTranslation("");
  const [serverSetup] = useState(sessionStorage.getObject(SERVER_SETUP));
  const baseUrl = `/${i18n.language || window.localStorage.i18nextLng}`;
  const categoryToLobbyRoute = (categoryName = "") => {
    const slug = gameCategoryToRoute(categoryName);
    return slug === "live-casino" && skin === "pub" ? "livecasino" : slug;
  };
  gamesForCategory =
    (skin === "pub" || skin === "cac" || skin === "hyc") && gamesForCategory;

  let hideNameForPub =
    (skin === "cac" || skin === "hyc") &&
    location.pathname === baseUrl + "/index"
      ? false
      : true;

  // Infinite scroll state - track visible games per category
  const INITIAL_GAMES_TO_SHOW = 20;
  const GAMES_INCREMENT = 20;
  const [visibleGamesPerCategory, setVisibleGamesPerCategory] = useState({});
  const observerRefs = useRef({});

  // Initialize visible games count for each category - reset on any gameLobby change
  useEffect(() => {
    if (gameLobby) {
      const initialCounts = {};
      gameLobby.forEach((category) => {
        initialCounts[category.categoryName] = INITIAL_GAMES_TO_SHOW;
      });
      setVisibleGamesPerCategory(initialCounts);
      // Clear observer refs when gameLobby changes
      observerRefs.current = {};
    }
  }, [gameLobby]);

  // Reset visible games for selected category changes
  useEffect(() => {
    if (!gameLobby) return;

    if (selectedCategory) {
      setVisibleGamesPerCategory((prev) => ({
        ...prev,
        [selectedCategory]: INITIAL_GAMES_TO_SHOW,
      }));
    }
  }, [selectedCategory, gameLobby]);

  // Load more games for a specific category
  const loadMoreGames = useCallback((categoryName) => {
    setVisibleGamesPerCategory((prev) => ({
      ...prev,
      [categoryName]:
        (prev[categoryName] || INITIAL_GAMES_TO_SHOW) + GAMES_INCREMENT,
    }));
  }, []);

  // Intersection Observer for infinite scroll - observe all sentinel elements
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryName = entry.target.getAttribute("data-category");
            if (categoryName) {
              loadMoreGames(categoryName);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      },
    );

    // Observe all current sentinel elements
    Object.values(observerRefs.current).forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [loadMoreGames, visibleGamesPerCategory]);

  // Callback ref to track sentinel elements
  const setSentinelRef = useCallback(
    (categoryName) => (element) => {
      if (element) {
        observerRefs.current[categoryName] = element;
      }
    },
    [],
  );

  // Helper function to get sliced games for a category
  const getVisibleGames = (category) => {
    let games = category.games;

    // Mobile homepage - show limited games (no infinite scroll)
    if (mobGamesInLobby) {
      return isTablet ? games.slice(0, 9) : games.slice(0, 4);
    }

    // Apply filters
    if (skin === "abc" && category.categoryName === "popular-games") {
      games = games.filter((el) => el.gameType !== "Live Casino");
    }

    // Apply infinite scroll slicing
    const visibleCount =
      visibleGamesPerCategory[category.categoryName] || INITIAL_GAMES_TO_SHOW;
    return games.slice(0, visibleCount);
  };

  // Check if category has more games to load
  const hasMoreGames = (category) => {
    // No infinite scroll for mobile homepage
    if (mobGamesInLobby) {
      return false;
    }

    const visibleCount =
      visibleGamesPerCategory[category.categoryName] || INITIAL_GAMES_TO_SHOW;
    let totalGames = category.games.length;

    if (skin === "abc" && category.categoryName === "popular-games") {
      totalGames = category.games.filter(
        (el) => el.gameType !== "Live Casino",
      ).length;
    }

    return visibleCount < totalGames;
  };

  const isCategoryHidden = (category) => {
    let selected =
      selectedCategory === undefined ||
      selectedCategory === category.categoryName ||
      selectedCategory === null ||
      (category.categoryName === "rapid-fire-jackpots" &&
        selectedCategory === "popular-games" &&
        (skin === "pub" || skin === "hyc" || skin === "cac")) ||
      ((category.categoryName === "game-of-week" ||
        category.categoryName === "game-of-month" ||
        category.categoryName === "game-of-year") &&
        selectedCategory === "popular-games")
        ? false
        : true;

    if (skin === "pub" && providerSearchFlag && category.games.length === 0) {
      selected = true;
    }

    return selected;
  };

  const isCategoryRenderedAsGrid = (category) =>
    !(category.categoryName === "themes" && themes) &&
    !(isRoyale99Category(category.categoryName) && selectedCategory === null) &&
    !(
      skin === "abc" &&
      (category.categoryName === "sports-book" ||
        category.categoryName === "live-casino")
    );

  let eagerBudget = 8;

  return (
    <>
      {gameLobby !== null &&
        gameLobby.map((category, index) => {
          const selected = isCategoryHidden(category);
          const isGameOfPromo =
            category.categoryName === "game-of-week" ||
            category.categoryName === "game-of-month" ||
            category.categoryName === "game-of-year";
          // Skip eager budget for gameof/ promos (often heavy animated GIFs)
          // so they do not compete with the hero LCP image.
          const eagerCount =
            isCategoryRenderedAsGrid(category) && !selected && !isGameOfPromo
              ? Math.min(eagerBudget, getVisibleGames(category).length)
              : 0;
          eagerBudget -= eagerCount;

          return category.categoryName === "themes" && themes ? (
            <>
              <GameCategoryHeader provider gameTheme>
                <GameCategoryTitle category>
                  {t(category.title, "Chose Theme:")}
                </GameCategoryTitle>
              </GameCategoryHeader>
              <GameThemeProviders
                setThemeSearchTerm={setThemeSearchTerm}
                themeSearchTerm={themeSearchTerm}
                setSearchResults={setSearchResults}
                skin={config}
                accountRoute={accountRoute}
                serverSetup={serverSetup}
                setSpinner={setSpinner}
                setSelectedCategory={setSelectedCategory}
                setThemeExists={setThemeExists}
              />
            </>
          ) : isRoyale99Category(category.categoryName) &&
            selectedCategory === null ? null : skin === "abc" &&
            (category.categoryName === "sports-book" ||
              category.categoryName === "live-casino") ? null : (
            <GameCategoryContent
              hide={selected}
              removeMargin={category.games.length}
              key={category.categoryName}
            >
              {category.title && hideNameForPub && (
                <GameCategoryHeader>
                  <CategoryTitle>
                    {t(
                      category.categoryName === "popular-games"
                        ? "label_popular"
                        : category.title,
                    )}
                  </CategoryTitle>
                  {skin !== "pub" &&
                    !gamesForCategory &&
                    category.categoryName !== "game-of-week" &&
                    category.categoryName !== "game-of-month" &&
                    category.categoryName !== "game-of-year" &&
                    category.categoryName !== "sports-book" && (
                      <Link
                        to={`/${
                          serverSetup.currentLanguage
                        }/${categoryToLobbyRoute(category.categoryName)}`}
                      >
                        <ShowAllGames>
                          {t("label_show_all")}
                          <ShowAllArrow>&rsaquo;</ShowAllArrow>
                        </ShowAllGames>
                      </Link>
                    )}
                </GameCategoryHeader>
              )}
              {category.title &&
                (skin === "cac" || skin === "hyc") &&
                window.location.pathname !==
                  `/${serverSetup.currentLanguage}/games` && (
                  <GameCategoryHeader index={true}>
                    {category.categoryName !== "game-of-week" &&
                      category.categoryName !== "game-of-month" &&
                      category.categoryName !== "game-of-year" && (
                        <Link
                          to={`/${
                            serverSetup.currentLanguage
                          }/${categoryToLobbyRoute(category.categoryName)}`}
                        >
                          <ShowAllGames>
                            {t("label_show_all")}
                            <ShowAllArrow>&rsaquo;</ShowAllArrow>
                          </ShowAllGames>
                        </Link>
                      )}
                  </GameCategoryHeader>
                )}
              {!gamesForCategory ? (
                <>
                  <GamesGriddy
                    remainder={index % 2}
                    specialGame={
                      category.categoryName === "game-of-week" ||
                      category.categoryName === "game-of-month" ||
                      category.categoryName === "game-of-year"
                    }
                  >
                    <Game
                      width={width}
                      categoryName={category.categoryName}
                      games={getVisibleGames(category)}
                      skin={skin}
                      eagerCount={eagerCount}
                      gameImagePath={
                        category.categoryName === "game-of-week" ||
                        category.categoryName === "game-of-month" ||
                        category.categoryName === "game-of-year"
                          ? gameImagePath + "gameof/"
                          : gameImagePath
                      }
                    />
                  </GamesGriddy>
                  {hasMoreGames(category) && (
                    <div
                      ref={setSentinelRef(category.categoryName)}
                      data-category={category.categoryName}
                      style={{ height: "20px", margin: "20px 0" }}
                    />
                  )}
                  {skin === "pub" && !providerSearchFlag && (
                    <AlignCategoryTitle>
                      {!gamesForCategory &&
                        category.categoryName !== "game-of-week" &&
                        category.categoryName !== "game-of-month" &&
                        category.categoryName !== "game-of-year" &&
                        (providerIsChosen ? (
                          <Link
                            to={`/${serverSetup.currentLanguage}/index`}
                            onClick={() =>
                              setSelectedGameType && setSelectedGameType(null)
                            }
                          >
                            <ShowAllGames>
                              {t("label_back_to_lobby")}
                              <ShowAllArrow>&lsaquo;</ShowAllArrow>
                            </ShowAllGames>
                          </Link>
                        ) : (
                          <Link
                            to={`/${
                              serverSetup.currentLanguage
                            }/${categoryToLobbyRoute(category.categoryName)}`}
                          >
                            <ShowAllGames>
                              {t("label_show_all")}
                              <ShowAllArrow>&rsaquo;</ShowAllArrow>
                            </ShowAllGames>
                          </Link>
                        ))}
                    </AlignCategoryTitle>
                  )}
                  {(skin === "cac" || skin === "hyc") && providerIsChosen && (
                    <AlignCategoryTitle>
                      {!gamesForCategory && (
                        <Link
                          to={`/${serverSetup.currentLanguage}/games`}
                          onClick={() => setSelectedGameType(null)}
                        >
                          <ShowAllGames>
                            {t("label_back_to_games")}
                            <ShowAllArrow>&lsaquo;</ShowAllArrow>
                          </ShowAllGames>
                        </Link>
                      )}
                    </AlignCategoryTitle>
                  )}
                </>
              ) : (
                <>
                  <GamesGriddy
                    opened={true}
                    specialGame={
                      category.categoryName === "game-of-week" ||
                      category.categoryName === "game-of-month" ||
                      category.categoryName === "game-of-year"
                    }
                  >
                    <Game
                      width={width}
                      categoryName={category.categoryName}
                      gamesForCategory={gamesForCategory}
                      games={getVisibleGames(category)}
                      skin={skin}
                      eagerCount={eagerCount}
                      gameImagePath={
                        category.categoryName === "game-of-week" ||
                        category.categoryName === "game-of-month" ||
                        category.categoryName === "game-of-year"
                          ? gameImagePath + "gameof/"
                          : gameImagePath
                      }
                    />
                  </GamesGriddy>
                  {hasMoreGames(category) && (
                    <div
                      ref={setSentinelRef(category.categoryName)}
                      data-category={category.categoryName}
                      style={{ height: "20px", margin: "20px 0" }}
                    />
                  )}
                </>
              )}
            </GameCategoryContent>
          );
        })}
      {location.pathname === `${baseUrl}/index` &&
        gameLobby &&
        gameLobby.filter((el) => el.games.length > 0).length === 0 && (
          <EmptyCategoriy hide={false}>
            <Telescope />
            <div> {t("label_unable_to_find")}</div>
            <div>
              {t(
                "label_unable_to_find_discription",
                "We are unable to find any games by selected provider",
              )}
            </div>
          </EmptyCategoriy>
        )}
    </>
  );
};

export default withRouter(Lobby);
